import isObject from 'lodash/isObject';

import FilterSelectList from '@/app/community/components/FilterSelectList.vue';
import MessageReceivedTimeForm from '@/app/community/components/inboxAutomation/MessageReceivedTimeForm.vue';
import TagEditableMenu from '@/app/community/components/TagEditableMenu.vue';
import {
  COMMUNITY_INTERACTION_FILTER_WIDGETS,
  COMMUNITY_INTERACTION_FILTERS,
} from '@/app/community/constants';
import { MESSAGE_CONTENT_FILTER_OPTIONS } from '@/app/community/constants/communityFilters';
import KeywordFilterForm from '@/app/community/drawers/components/automation/KeywordFilterForm.vue';
import { isDmRefactorEnabled } from '@/app/community/utils/dmRefactorFlag';
import { parseBoolean } from '@/app/community/utils/store-utils';

export const MESSAGE_RULE_TYPES = {
  FILTER: 'FILTER',
  ACTION: 'ACTION',
};

export const MESSAGE_RULE_OPERATOR_TYPES = {
  AND: 'AND',
  OR: 'OR',
};

export const AUTOMATION_RULE_OPERATOR_OPTIONS = {
  [MESSAGE_RULE_OPERATOR_TYPES.AND]: {
    label: 'And',
    value: MESSAGE_RULE_OPERATOR_TYPES.AND,
  },
  [MESSAGE_RULE_OPERATOR_TYPES.OR]: {
    label: 'Or',
    value: MESSAGE_RULE_OPERATOR_TYPES.OR,
  },
};

const filterOptions = (filterType) =>
  COMMUNITY_INTERACTION_FILTER_WIDGETS[filterType].options?.filterOptions;

export const tagOptionFormatter = (tag) => ({
  label: tag.name,
  icon: {
    name: 'dot',
    color: tag.color,
  },
  value: tag.id,
  tooltip: true,
});

const tagWidget = {
  options: [],
  formatOption: (option) => tagOptionFormatter(option),
};

export const MESSAGE_CONTENT_BOOLEAN_OPTIONS = {
  QUESTION: {
    label: 'Question',
    value: 'QUESTION',
    key: 'is_question',
  },
  SPAM: {
    label: 'Spam',
    value: 'SPAM',
    info: 'Applies to messages automatically marked as spam by AI or manually by users',
    key: 'is_spam',
  },
};

export const MESSAGE_RULE_WIDGET_TYPES = {
  SELECT: 'SELECT',
  INPUT: 'INPUT',
  SELECT_INPUT: 'SELECT_INPUT',
};

export const KEYWORD_ENTIRE_MESSAGE_LABEL = 'Keyword (Entire Message)';

export const MESSAGE_RULE_FILTER_OPTIONS = {
  CHANNEL: {
    label: 'Channel',
    labelOperator: 'is',
    key: 'platform',
    widget: {
      type: MESSAGE_RULE_WIDGET_TYPES.SELECT,
      options: filterOptions(COMMUNITY_INTERACTION_FILTERS.FILTER_PLATFORM),
      formatValue: (value) => value?.value,
    },
    form: FilterSelectList,
  },
  KEYWORD: {
    label: 'Keyword',
    labelOperator: 'is',
    key: 'text',
    widget: {
      type: MESSAGE_RULE_WIDGET_TYPES.INPUT,
    },
    info: 'Specify keywords that appear in the message. Enable One-Word Messages Only to match messages where the entire message is that one word.',
    form: KeywordFilterForm,
  },
  MESSAGE_CONTENT: {
    label: 'Message Content',
    labelOperator: 'is',
    key: 'message_content',
    info: 'Matches messages made up of a single element, like an emoji or mention.',
    widget: {
      type: MESSAGE_RULE_WIDGET_TYPES.SELECT,
      options: [
        MESSAGE_CONTENT_FILTER_OPTIONS.EMOJI_ONLY,
        MESSAGE_CONTENT_FILTER_OPTIONS.GIF_ONLY,
        MESSAGE_CONTENT_FILTER_OPTIONS.HASHTAG_ONLY,
        MESSAGE_CONTENT_FILTER_OPTIONS.LINK_ONLY,
        MESSAGE_CONTENT_FILTER_OPTIONS.MENTION_ONLY,
        MESSAGE_CONTENT_FILTER_OPTIONS.QUESTION,
        MESSAGE_CONTENT_FILTER_OPTIONS.SPAM,
        MESSAGE_CONTENT_FILTER_OPTIONS.UNSUPPORTED,
      ],
      formatValue: (value) => value?.value,
    },
    form: FilterSelectList,
  },
  MESSAGE_TYPE: {
    label: 'Message Type',
    labelOperator: 'is',
    key: 'message_type',
    widget: {
      type: MESSAGE_RULE_WIDGET_TYPES.SELECT,
      options: filterOptions(COMMUNITY_INTERACTION_FILTERS.FILTER_MESSAGE_TYPE),
      formatValue: (value) => value?.value,
    },
    form: FilterSelectList,
  },
  QUESTION: {
    label: 'Question',
    labelOperator: 'is',
    key: 'is_question',
    widget: {
      type: MESSAGE_RULE_WIDGET_TYPES.SELECT,
      options: filterOptions(COMMUNITY_INTERACTION_FILTERS.FILTER_QUESTIONS).map((v) => ({
        ...v,
        value: parseBoolean(v.value),
      })),
      formatValue: (value) => value?.value,
    },
  },
  REPLIED: {
    label: 'Replied',
    key: 'is_replied',
    value: true,
  },
  SENTIMENT: {
    label: 'Sentiment',
    labelOperator: 'is',
    key: 'sentiment',
    widget: {
      type: MESSAGE_RULE_WIDGET_TYPES.SELECT,
      options: filterOptions(COMMUNITY_INTERACTION_FILTERS.FILTER_SENTIMENT),
      formatValue: (value) => value?.value,
    },
    form: FilterSelectList,
  },
  TAG: {
    label: 'Tag',
    labelOperator: 'is',
    key: 'tag',
    widget: {
      type: MESSAGE_RULE_WIDGET_TYPES.SELECT_INPUT,
      ...tagWidget,
      formatValue: (value) => ({ [`${value.value}`]: value.label }),
      getValue: (value) => (isObject(value) ? Object.keys(value)[0] : value),
    },
    form: TagEditableMenu,
  },
};

export const AI_CATEGORY_FILTER_OPTION = {
  label: 'Vision AI Prompt',
  key: 'ai_category_id',
  previewKey: 'ai_category_prompt',
  widget: {
    type: MESSAGE_RULE_WIDGET_TYPES.INPUT,
  },
};

export const BUSINESS_HOURS = {
  DURING_BUSINESS_HOURS: true,
  OFF_HOURS: false,
};

const MESSAGE_RECEIVED_TIME_OPTIONS = [
  { label: 'Off-Hours', value: BUSINESS_HOURS.OFF_HOURS },
  { label: 'Business Hours', value: BUSINESS_HOURS.DURING_BUSINESS_HOURS },
];

export const MESSAGE_RECEIVED_TIME_FILTER_OPTION = {
  label: 'Message Received Time',
  key: 'received_during_business_hours',
  widget: {
    type: MESSAGE_RULE_WIDGET_TYPES.SELECT,
    options: MESSAGE_RECEIVED_TIME_OPTIONS,
    formatValue: (value) => value?.value,
  },
  form: MessageReceivedTimeForm,
};

export const MESSAGE_RULE_ACTION_OPTIONS = {
  ASSIGN_TO_USER: {
    label: 'Assign to User',
    key: 'assigneeId',
  },
  ARCHIVE: {
    get label() {
      return isDmRefactorEnabled() ? 'Close' : 'Archive';
    },
    key: 'archive',
    value: true,
  },
  CREATE_SALESFORCE: {
    label: 'Create Salesforce Case',
    key: 'integration',
    value: 'salesforce',
  },
  DELETE: {
    get label() {
      return isDmRefactorEnabled() ? 'Delete and Close Comments' : 'Delete and Archive Comments';
    },
    key: 'delete',
    value: true,
    info: 'Applicable to Instagram, Facebook, and YouTube. Not available for TikTok or X. Deleting a comment will also delete any replies to that comment. It may take up to 10 seconds for the comment to be deleted.',
  },
  HIDE: {
    get label() {
      return isDmRefactorEnabled() ? 'Hide and Close Comments' : 'Hide and Archive Comments';
    },
    key: 'hide',
    value: true,
    info: 'Applicable to Instagram, Facebook, TikTok, and X. Not available for YouTube. Hiding a comment will also hide any replies to that comment. It may take up to 10 seconds for the comment to be hidden.',
  },
  LIKE_COMMENTS: {
    label: 'Like Comments',
    key: 'like',
    value: true,
    info: 'Applicable to Instagram, Facebook and TikTok. Not available for YouTube or X.',
  },
  SEND_SAVED_REPLY: {
    label: 'Send Automatic Reply',
    key: 'presetReplyId',
  },
  TAG: {
    label: 'Tag',
    key: 'tags',
    widget: {
      type: MESSAGE_RULE_WIDGET_TYPES.SELECT_INPUT,
      ...tagWidget,
      formatValue: (value) => [{ [`${value.value}`]: value.label }],
      getValue: (value) => (isObject(value[0]) ? Object.keys(value[0])[0] : value),
    },
    formatAction: (value) => ({ [`${value.value}`]: value.label }),
  },
  SEND_DM_FROM_COMMENT: {
    label: 'Send DM from Comment',
    key: 'privateReplyPresetReplyId',
  },
};

export const RULE_ACTION_KEYS = {
  ARCHIVE: 'archive',
  DELETE: 'delete',
  HIDE: 'hide',
  LIKE: 'like',
  TAGS: 'tags',
  PRESET_REPLY_ID: 'presetReplyId',
  PRIVATE_REPLY_PRESET_REPLY_ID: 'privateReplyPresetReplyId',
  ASSIGNEE_ID: 'assigneeId',
};

export const RULE_ACTION_TEXT_MAP = {
  get [RULE_ACTION_KEYS.ARCHIVE]() {
    return isDmRefactorEnabled() ? 'closed' : 'archived';
  },
  [RULE_ACTION_KEYS.DELETE]: 'deleted',
  [RULE_ACTION_KEYS.HIDE]: 'hidden',
  [RULE_ACTION_KEYS.LIKE]: 'liked',
  [RULE_ACTION_KEYS.TAGS]: 'tagged',
  [RULE_ACTION_KEYS.PRESET_REPLY_ID]: 'Reply automatically sent',
  [RULE_ACTION_KEYS.PRIVATE_REPLY_PRESET_REPLY_ID]: 'DM sent from Comment',
  [RULE_ACTION_KEYS.ASSIGNEE_ID]: 'assigned',
};

export const MESSAGE_RULE_INIT_OPTIONS = {
  [MESSAGE_RULE_TYPES.FILTER]: {
    label: 'Add Filter',
    optionsMap: MESSAGE_RULE_FILTER_OPTIONS,
  },
  [MESSAGE_RULE_TYPES.ACTION]: {
    label: 'Add Action',
    optionsMap: MESSAGE_RULE_ACTION_OPTIONS,
  },
};

export const messageRuleFilterByKey = (key) => {
  const messageContentBooleanKeys = Object.values(MESSAGE_CONTENT_BOOLEAN_OPTIONS).map(
    (o) => o.key,
  );
  if (messageContentBooleanKeys.includes(key)) {
    return MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_CONTENT;
  }
  if (key === MESSAGE_RECEIVED_TIME_FILTER_OPTION.key) {
    return MESSAGE_RECEIVED_TIME_FILTER_OPTION;
  }
  return Object.values(MESSAGE_RULE_FILTER_OPTIONS).find((o) => o.key === key);
};

export const MESSAGE_RULES_POPUP_MESSAGES = {
  EMPTY_STATE: 'Automate your Community inbox',
};

export const DEPRECATED_RULE_NAME = 'Archive by Message Type Preset';

export const MESSAGE_RULE_BUTTON_TOOLTIP =
  'Create custom automations to action messages in your Community inbox';

export const RULE_EXECUTION_NOTIFICATION_SUBSTRINGS = {
  ARCHIVE: 'moved to Archive', // TODO: DM_REFACTOR_2026 - Remove, use CLOSED
  CLOSED: 'closed',
  TAGGED: 'has been Tagged',
  DELETED: 'has been Deleted',
  HIDDEN: 'has been Hidden',
  DM_SENT: 'DM sent from Comment',
  FAILED_TO_DELETE: 'failed to Delete',
  FAILED_TO_HIDE: 'failed to Hide',
  FAILED_TO_SEND_DM: 'failed to send DM',
};
export const CREATE_RULE_DISABLED_TOOLTIP = "You've reached your limit of 25 automation rules";

export const MAX_MESSAGE_RULE = 25;

export const MAX_AI_CATEGORY = 10;
export const POWER_USER_MAX_AI_CATEGORY = 20;

// Brand IDs that are allowed to create up to POWER_USER_MAX_AI_CATEGORY AI categories
export const AI_CATEGORY_POWER_USER_BRAND_IDS = [
  '334',
  '14699',
  '26740',
  '26747',
  '26748',
  '26749',
];

export function getMaxAiCategory(brandId) {
  const brandIdString = String(brandId);
  return AI_CATEGORY_POWER_USER_BRAND_IDS.includes(brandIdString)
    ? POWER_USER_MAX_AI_CATEGORY
    : MAX_AI_CATEGORY;
}

export const PREVIEW_INTERACTIONS_LIMIT = 250;

export const PREVIEW_EMPTY_STATE_DETAILS = {
  NO_FILTER_MATCHES: {
    message:
      'None of your historical messages match the filters. Review the and/or conditions to ensure they work together and try again.',
  },
  NO_AI_PROMPT_MATCHES: {
    title: 'No Recent Messages Found',
    message:
      "Try narrowing your filters as the Vision AI Prompt didn't match any recent messages. You can still create the rule and apply it to new and recent messages, but matches aren't guaranteed.",
  },
};

export const PREVIEW_BLOCKED_DETAILS = {
  BUSINESS_HOURS: {
    title: 'Preview Not Available For Business Hours Filter',
    message: 'Continue to the next step to enable your Automation Rule for new incoming messages.',
    icon: 'eye-off',
  },
};

export const getRuleItemLabel = (key, isFilter = true) => {
  const options = isFilter
    ? { ...MESSAGE_RULE_FILTER_OPTIONS, AI_CATEGORY_FILTER_OPTION }
    : MESSAGE_RULE_ACTION_OPTIONS;
  const item = Object.values(options).find((o) => o.key === key);
  return item?.label;
};

export const MESSAGE_RULE_SAVE_OPTIONS = {
  SAVE: 'Save',
  SAVE_AND_APPLY_TO_EXISTING: 'Save & Apply to Existing',
};

export const MESSAGE_RULE_MIXPANEL_CONSTANTS = {
  RULE_BUILDER_OPENED_FROM: {
    CREATE: 'Create Rule Button Clicked',
    EDIT: 'Edit Button Clicked',
    EDIT_FROM_MESSAGE: 'Edit Button from Message Clicked',
  },
  MESSAGE_RULE_INTERACTIONS: {
    EDIT: 'Edit',
    EDIT_FROM_MESSAGE: 'Edit From Message',
    DELETE: 'Delete',
    VIEW_ALL: 'View All',
    TOGGLE_ON: 'Toggle On',
    TOGGLE_OFF: 'Toggle Off',
  },
};
export const COMMUNITY_INTERACTION_RULE_TOASTS = {
  SUCCESS: 'Your automation rule has been saved',
  SUCCESS_REACTIVATED: 'Your automation rules have been saved and re-activated',
  APPLY_TO_EXISTING: 'It may take a few moments to apply it to all messages.',
  FAILURE: 'There was an issue saving your automation rules, please try again',
  DELETED: {
    SUCCESS: 'Your rule has been deleted',
    FAILURE: 'There was an issue deleting your rule. Please try again.',
  },
  UNDO: {
    SUCCESS: 'Undo successful. Message restored to its original state in your Inbox.',
    ERROR: 'Undo failed. Please try again.',
  },
};

export const messageRuleToggleToast = (to) =>
  `Your automation rule has been ${to ? 'enabled' : 'disabled'}`;

export const MESSAGE_RULE_FILTER_KEY_MAP = Object.freeze({
  [MESSAGE_RULE_FILTER_OPTIONS.CHANNEL.key]: MESSAGE_RULE_FILTER_OPTIONS.CHANNEL,
  [MESSAGE_RULE_FILTER_OPTIONS.KEYWORD.key]: MESSAGE_RULE_FILTER_OPTIONS.KEYWORD,
  [MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_CONTENT.key]: MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_CONTENT,
  [MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_TYPE.key]: MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_TYPE,
  [MESSAGE_RULE_FILTER_OPTIONS.REPLIED.key]: MESSAGE_RULE_FILTER_OPTIONS.REPLIED,
  [MESSAGE_RULE_FILTER_OPTIONS.SENTIMENT.key]: MESSAGE_RULE_FILTER_OPTIONS.SENTIMENT,
  [MESSAGE_RULE_FILTER_OPTIONS.TAG.key]: MESSAGE_RULE_FILTER_OPTIONS.TAG,
  [AI_CATEGORY_FILTER_OPTION.key]: AI_CATEGORY_FILTER_OPTION,
  [MESSAGE_RECEIVED_TIME_FILTER_OPTION.key]: MESSAGE_RECEIVED_TIME_FILTER_OPTION,
});

export const MESSAGE_RULE_ACTION_KEY_MAP = Object.freeze({
  [MESSAGE_RULE_ACTION_OPTIONS.ARCHIVE.key]: MESSAGE_RULE_ACTION_OPTIONS.ARCHIVE,
  [MESSAGE_RULE_ACTION_OPTIONS.CREATE_SALESFORCE.key]:
    MESSAGE_RULE_ACTION_OPTIONS.CREATE_SALESFORCE,
  [MESSAGE_RULE_ACTION_OPTIONS.DELETE.key]: MESSAGE_RULE_ACTION_OPTIONS.DELETE,
  [MESSAGE_RULE_ACTION_OPTIONS.HIDE.key]: MESSAGE_RULE_ACTION_OPTIONS.HIDE,
  [MESSAGE_RULE_ACTION_OPTIONS.LIKE_COMMENTS.key]: MESSAGE_RULE_ACTION_OPTIONS.LIKE_COMMENTS,
  [MESSAGE_RULE_ACTION_OPTIONS.SEND_SAVED_REPLY.key]: MESSAGE_RULE_ACTION_OPTIONS.SEND_SAVED_REPLY,
  [MESSAGE_RULE_ACTION_OPTIONS.TAG.key]: MESSAGE_RULE_ACTION_OPTIONS.TAG,
  [MESSAGE_RULE_ACTION_OPTIONS.SEND_DM_FROM_COMMENT.key]:
    MESSAGE_RULE_ACTION_OPTIONS.SEND_DM_FROM_COMMENT,
  [MESSAGE_RULE_ACTION_OPTIONS.ASSIGN_TO_USER.key]: MESSAGE_RULE_ACTION_OPTIONS.ASSIGN_TO_USER,
});

// Actions that already include closing the comment (Close itself, or the
// "... and Close Comments" variants) can't take Close as a secondary action.
// Every other action can, so this is a denylist rather than an allowlist that
// has to be updated whenever a new action is added.
export const CLOSE_SECONDARY_ACTION_DENYLIST = [
  MESSAGE_RULE_ACTION_OPTIONS.ARCHIVE.key,
  MESSAGE_RULE_ACTION_OPTIONS.DELETE.key,
  MESSAGE_RULE_ACTION_OPTIONS.HIDE.key,
];

export const MESSAGE_RULE_APPLY_OPTIONS = {
  NEW: {
    label: 'Apply to New Messages Only',
    value: 'NEW',
    applyToHistorical: false,
  },
  HISTORICAL: {
    label: 'Apply to New and Existing Messages',
    value: 'HISTORICAL',
    applyToHistorical: true,
  },
};

export const MESSAGE_RULE_CREATE_TOAST = {
  NEW: {
    message: 'Automation Rule created',
  },
  HISTORICAL: {
    title: 'Automation Rule Created',
    message: 'It may take a few moments to apply to existing messages.',
  },
};

export const MESSAGE_RULE_EDIT_TOAST = {
  NEW: {
    message: 'Automation Rule edited',
  },
  HISTORICAL: {
    title: 'Automation Rule Edited',
    message: 'It may take a few moments to apply to existing messages.',
  },
};

export const MESSAGE_RULE_CREATE_TOAST_TASK = {
  NEW: {
    message: 'Task created',
  },
  HISTORICAL: {
    title: 'Task Created',
    message: 'It may take a few moments to apply to existing messages.',
  },
};

export const MESSAGE_RULE_EDIT_TOAST_TASK = {
  NEW: {
    message: 'Task edited',
  },
  HISTORICAL: {
    title: 'Task Edited',
    message: 'It may take a few moments to apply to existing messages.',
  },
};

export const RULE_BUILDER_BANNER = {
  DELETE: {
    severity: 'error',
    message: 'Deleting comments is not available for TikTok or X. Remove that channel to continue.',
  },
  HIDE: {
    severity: 'error',
    message: 'Hiding comments is not available for YouTube. Remove that channel to continue.',
  },
  CREATE_SALESFORCE: {
    severity: 'warn',
    message:
      "Use specific filters to control case creation. Broad rules like 'all messages' will create a case for most interactions, quickly exceeding your Salesforce API limits.",
  },
  SEND_DM_FROM_COMMENT: {
    severity: 'info',
    message:
      'Meta limits the number of automated DMs to 750 per hour per account. Notify us if your DMs are failing due to this limit and we can bulk resend.',
  },
  SEND_SAVED_REPLY: {
    severity: 'info',
    message: 'This message will only be sent once to the same user within a 7 day period.',
  },
  STORY_MENTION_AUTO_REPLY: {
    severity: 'warn',
    message: 'Automatic replies will not be sent for any Instagram Story Mentions.',
  },
  LIKE_COMMENTS_MISSING_SCOPE: {
    severity: 'error',
    message:
      'You are missing a permission required to like Instagram comments. Disconnect and reconnect your Instagram account to gain access and continue.',
  },
};

export const AI_CATEGORY_PRESET_OPTIONS = Object.freeze([
  {
    label: 'Create Your Own',
    icon: 'draft',
    form: {
      name: '',
      prompt: '',
    },
  },
  {
    label: 'Brand Love',
    icon: 'heart',
    form: {
      name: 'Brand Love',
      prompt:
        'Brand love messages where users are complimenting or expressing their love and admiration for the brand, product or service.\n\nExclude messages that include a question or request an action.',
    },
  },
  {
    label: 'Sales Intent',
    icon: 'currency-dollar',
    form: {
      name: 'Sales Intent',
      prompt:
        'Sales intent messages where users indicate their interest in purchasing the product or service - they did not buy it already. Include inquiries about the product details, availability, or where to buy it.\n\nExclude messages where the user is referring to investments, driving sales or interest in the product, or anything that is a business proposal or promotional sales pitch.',
    },
  },
  {
    label: 'Collab Requests',
    icon: 'users',
    form: {
      name: 'Collab Requests',
      prompt:
        'Collab request messages where users are asking to collaborate on content creation for the brands product or service.\n\nExclude messages where users are asking to friend them or follow them. Exclude messages that are spam or promotional offers for services unrelated to the brand.',
    },
  },
  {
    label: 'Customer Support',
    icon: 'message-question',
    form: {
      name: 'Customer Support',
      prompt:
        'Customer support messages where users ask for help or support with a recent purchase of the product or service. This may include shipping delays, order issues, or inquiring about a refund or exchange.\n\nExclude messages where they only ask for product details or are only complaining about the product.',
    },
  },
]);

export const AI_CATEGORY_DISCARD_DIALOG = {
  group: 'global',
  header: 'Discard Vision AI Prompt?',
  message: 'Are you sure you want to discard this Vision AI Prompt?',
  acceptLabel: 'Discard',
  acceptSeverity: 'danger',
};

export const AI_CATEGORY_REMOVE_DIALOG = {
  group: 'global',
  header: 'Remove Vision AI Prompt?',
  message: 'Are you sure you want to remove this Vision AI Prompt?',
  acceptLabel: 'Delete',
  acceptSeverity: 'danger',
};

export const getDisabledHistoricalOptionProps = (tooltipStr, isTask = false) => ({
  disabled: true,
  info: `${
    isTask ? 'Tasks' : 'Automations'
  } using ${tooltipStr} can’t be applied to existing messages — only new ones.`,
});

export const AI_CATEGORY_FORM_HINT = {
  NEW: 'The prompt should include examples of what messages should include and also exclude. Preview matching messages in Step 2, then continue to refine the prompt until results are accurate.',
  READ_ONLY:
    'Editing prompts isn’t currently supported. To update a prompt: copy it, remove the purple AI filter, add a new AI filter, paste and edit, then save. Other filters and the action won’t be affected.',
};

export const SEND_SAVED_REPLY_TOOLTIP = {
  NO_CHANNEL:
    'Channels must be associated to saved replies. Add a Channel filter to select this action.',
  TOO_MANY_CHANNELS:
    'Only one channel can be associated to a saved reply. Remove extra channels to continue.',
  CHANNEL_OR_CONDITION:
    "The channel filter must not be joined with another filter using ‘or’, as that makes the channel optional. Use the 'and' condition instead.",
};

export const SEND_DM_FROM_COMMENT_VALIDATION_REASONS = {
  UNSUPPORTED_CHANNEL: 'UNSUPPORTED_CHANNEL',
  CHANNEL_IN_EACH_BRANCH: 'CHANNEL_IN_EACH_BRANCH',
  MESSAGE_TYPE: 'MESSAGE_TYPE',
  KEYWORD: 'KEYWORD',
  KEYWORD_ONE_WORD: 'KEYWORD_ONE_WORD',
};

export const SEND_DM_FROM_COMMENT_REQUIREMENT_LABELS = {
  CHANNEL: 'Channel = Instagram or Facebook',
  MESSAGE_TYPE: 'Message Type = Comments or Ad Comments',
  KEYWORD: 'Keyword = One-Word Messages Only',
};

export const LIKE_COMMENTS_VALIDATION_REASONS = {
  UNSUPPORTED_CHANNEL: 'UNSUPPORTED_CHANNEL',
  MESSAGE_TYPE: 'MESSAGE_TYPE',
};

export const LIKE_COMMENTS_REQUIREMENT_LABELS = {
  CHANNEL: 'Channel = Instagram, Facebook, or TikTok',
  MESSAGE_TYPE: 'Message Type = Comments or Ad Comments',
};

export const TAG_USED_IN_AUTOMATION_RULE_TOOLTIP =
  'This tag can’t be deleted because it’s used in an Automation Rule. Remove the tag from the rule or delete the rule first.';

export const REPLY_USED_IN_AUTOMATION_RULE_TOOLTIP =
  'This reply can’t be deleted because it’s used in an Automation Rule. Change the reply in the rule or delete the rule first.';
